import Image from "next/image";
import Link from "next/link";
import {
  createRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getOptionBySlug, getOptions } from "../../lib/options";
import { BookTourFormContext } from "../../hooks/useBookTourForm";
import { getSEOPageProps } from "../../lib/seo";
import { getLocations } from "../../lib/locations";

import Button from "../../components/button";
import CompareOptionItem from "../../components/compare-option-item";
import Heading from "../../components/heading";
import HowToGetStarted from "../../components/how-to-get-started";
import LocationListItem from "../../components/location-list-item";
import MeetOurClients from "../../components/meet-our-clients";
import ProductDetailsModal from "../../components/product-details-modal";
import SeoMeta from "../../components/seo-meta";
import BoardroomFormModal from "../../components/boardroom-form-modal";
import EventFormModal from "../../components/event-form-modal";
import BoardroomMobileForm from "../../components/boardroom-mobile-form";
import EventMobileForm from "../../components/event-mobile-form";
import BookingForm from "../../components/booking-form";

import styles from "./option.module.scss";
import useViewport, { Viewport } from "../../hooks/useViewport";
import LinkButton from "../../components/link-button/link-button";
import config from "../../config";

export default function OptionPage({ option, locations, seo }) {
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [showBoardroomModal, setShowBoardroomModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const compareRefs = useRef(
    (option.compareOptionsCollection?.items || []).map(() => createRef())
  );

  const { setBookTourFormOpen } = useContext(BookTourFormContext);

  const compareOptions = useMemo(() => {
    const items = option.compareOptionsCollection?.items || [];

    if (items.length > 1) {
      return items;
    }

    // Default to current option and first compare option
    if (items.length === 1) {
      return [option, items[0]];
    }

    // else, no comparison
    return [];
  }, [option]);

  useEffect(() => {
    if (compareRefs.current?.length > 0) {
      // compare heights.
      const nextHeights = compareRefs.current
        .filter(ref => ref.current)
        .map((ref) => ref.current.getBoundingClientRect().height);

      if (nextHeights.length > 0) {
        const maxHeight = Math.max(...nextHeights);

        compareRefs.current
          .filter(ref => ref.current)
          .forEach((ref) => {
            ref.current.style.height = `${maxHeight}px`;
          });
      }
    }
  }, [option]);

  const viewport = useViewport();

  return (
    <>
      <div className="container">
        <SeoMeta {...seo} />
        <div className={styles.Hero}>
          <div className={styles.ProductInfo}>
            <div className={styles.ProductTitle}>
              {/* Option name */}
              <h3>{option.solution}</h3>
              <h2>{option.name}</h2>
            </div>
            <div className={styles.MobileImageContainer}>
              <div className={styles.MobileImage}>
                {/* Mobile image */}
                <Image
                  src={`${option.image?.url}?w=600&fm=webp&q=80`}
                  width="600"
                  height="600"
                  layout="responsive"
                  alt={option.image?.description}
                />
              </div>
            </div>

            <div className={styles.Details}>
              {/* Pricing details */}

              <div className={styles.ProductDetailsHeading}>
                <h4>{option.description}</h4>
              </div>

              <div className={styles.Description}>
                <p>{option.price}</p>
              </div>
            </div>

            <div className={styles.ProductDetailsHeading}>
              {/* Product details */}
              <h4>Product Details</h4>

              <a
                className="underline"
                onClick={() => setShowProductDetails(true)}
              >
                View All
              </a>
            </div>

            <div className={styles.Description}>
              <p>{option.productDetails}</p>
            </div>

            <div className={styles.BookTour}>
              {option.formType === "Book a Tour" && (
                <Button
                  outline
                  rounded
                  small
                  onClick={() => setBookTourFormOpen(true)}
                >
                  Book a Tour
                </Button>
              )}

              {option.formType === "Event" && (
                <Button
                  outline
                  rounded
                  small
                  onClick={() => setShowEventModal(true)}
                >
                  Inquire
                </Button>
              )}

              {option.formType === "Boardroom" && (
                <Button
                  outline
                  rounded
                  small
                  onClick={() => setShowBoardroomModal(true)}
                >
                  Inquire
                </Button>
              )}
            </div>

            {option.attachment && (
              <div className="flex flex-col items-center justify-center mt-8">
                <LinkButton
                  outline
                  rounded
                  small
                  href={option.attachment.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {option.attachmentText || "Learn More"}
                </LinkButton>
              </div>
            )}
          </div>
          <div className={styles.DesktopImageContainer}>
            {/* Desktop image. */}
            <div className={styles.DesktopImage}>
              <Image
                src={`${option.image?.url}?w=1200&fm=webp&q=80`}
                alt={option.image?.description}
                layout="fill"
              />
            </div>
          </div>
        </div>

        {option?.mainFeaturesCollection?.items?.length > 0 && (
          <div className={styles.Features}>
            {(option.mainFeaturesCollection?.items || []).map((feature) => (
              <div className={styles.Feature} key={feature.name}>
                <div className={styles.ImageContainer}>
                  <Image
                    src={`${feature.image?.url}?w=800&fm=webp&q=80`}
                    alt={feature.image?.description || ""}
                    layout="responsive"
                    width="700"
                    height="436"
                    objectFit="cover"
                  />
                </div>
                <div className={styles.FeatureContent}>
                  <h2>{feature.title}</h2>

                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <HowToGetStarted steps={option?.howToGetStartedCollection?.items} />

      {compareOptions.length > 0 && (
        <div className="container">
          <div className={styles.CompareOptions}>
            <div className={styles.CompareOptionsHeading}>
              <Heading small>Compare Membership Options</Heading>
              <Link href="/solutions" passHref>
                <a>View All</a>
              </Link>
            </div>

            <div className={styles.CompareOptionsList}>
              {compareOptions.map((compareOption, index) => (
                <div
                  className={styles.CompareOptionsItem}
                  key={compareOption.slug}
                >
                  <CompareOptionItem
                    option={compareOption}
                    ref={compareRefs.current[index]}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Locations */}

      <div className="container">
        <div className={styles.Locations}>
          <Heading small>View Available Locations</Heading>

          <div className={styles.LocationsList}>
            {(option.locationsCollection?.items || []).map((location) => (
              <LocationListItem key={location.slug} location={location} />
            ))}
          </div>

          <Link href="/locations" passHref>
            <Button outline rounded small>
              View All Locations
            </Button>
          </Link>
        </div>
      </div>

      <MeetOurClients />

      <ProductDetailsModal
        isOpen={showProductDetails}
        option={option}
        onRequestClose={() => setShowProductDetails(false)}
      />

      {option.formType === "Book a Tour" && <BookingForm hideDesktop />}

      {option.formType === "Boardroom" && (
        <BoardroomMobileForm locations={locations} />
      )}
      {option.formType === "Event" && <EventMobileForm locations={locations} />}

      {showBoardroomModal && (
        <BoardroomFormModal
          lightbox={viewport !== Viewport.PHONE}
          locations={locations}
          isOpen={showBoardroomModal}
          onClose={() => setShowBoardroomModal(false)}
          onRequestClose={() => setShowBoardroomModal(false)}
        />
      )}

      {showEventModal && (
        <EventFormModal
          lightbox={viewport !== Viewport.PHONE}
          locations={locations}
          isOpen={showEventModal}
          onRequestClose={() => setShowEventModal(false)}
        />
      )}
    </>
  );
}

// 25 options, linked from the nav. Prerendered in full so the first visitor
// after a deploy never waits.
export async function getStaticPaths() {
  const options = await getOptions();

  return {
    paths: (options || []).map((option) => ({ params: { slug: option.slug } })),
    fallback: "blocking"
  };
}

export async function getStaticProps({ params }) {
  try {
    const [option, locations, seo] = await Promise.all([
      getOptionBySlug(params.slug),
      getLocations(),
      getSEOPageProps({ resolvedUrl: `/options/${params.slug}` })
    ]);

    if (!option) {
      return { notFound: true, revalidate: config.NOT_FOUND_TTL };
    }

    return {
      props: { option, locations, seo },
      revalidate: config.ISR_TIMING
    };
  } catch (e) {
    console.log(e.message);
    return { notFound: true, revalidate: config.NOT_FOUND_TTL };
  }
}

